import { getContacts, getOneContact, getUserContact } from '@/api/contacts.api';
import { getUserDetails } from '@/api/user.api';
import ContactQuickSearch from '@/components/contact/ContactQuickSearch.vue';
import { showModal } from '@/shared/helpers';
import type { IContactPerson, IFetchContactParams, IUserCompanyFunction } from '@condo/domain';
import { arrify } from '@condo/helpers';
import { useQuery } from '@tanstack/vue-query';
import { NButton } from 'naive-ui';
import { type MaybeRef, computed, defineComponent, h, ref, unref } from 'vue';
import { useStore } from 'vuex';

export const useContacts = (paramsOrRef: MaybeRef<IFetchContactParams | undefined>) => {
    const params = computed(() => unref(paramsOrRef));
    const enabled = computed(() => !!params.value);

    return useQuery(
        ['contacts', params],
        async () => {
            const contactData = await getContacts(params.value as IFetchContactParams);
            return contactData?.contacts ?? [];
        },
        {
            initialData: [],
            enabled,
        },
    );
};

export const useContactLabelByContactId = (
    contactIdOrRef: MaybeRef<number | null | undefined>,
    opts?: Partial<NonNullable<Parameters<typeof useQuery>[2]>>,
) => {
    const contactId = computed(() => unref(contactIdOrRef));

    const query = useQuery(
        ['contacts', 'by-contact-id', contactId],
        async () => {
            const { contact } = await getOneContact(contactId.value as number);
            return contact ?? null;
        },
        {
            initialData: null,
            ...opts,
        } as any,
    );

    const label = computed(() => {
        if (!contactId.value) return 'User';
        const contact = query.data.value;
        return contact?.fullname || [contact?.firstname, contact?.lastname].filter(Boolean).join(' ') || 'Unknown';
    });
    return { ...query, label };
};

export const useContactLabelByUserId = (userIdOrRef: MaybeRef<string | null | undefined>, opts?: Partial<NonNullable<Parameters<typeof useQuery>[2]>>) => {
    const userId = computed(() => unref(userIdOrRef));

    const query = useQuery(
        ['contacts', 'by-user-id', userId],
        async () => {
            const { contact } = await getUserContact(userId.value as string);
            return contact ?? null;
        },
        {
            initialData: null,
            ...opts,
        } as any,
    );

    const label = computed(() => {
        if (!userId.value) return 'Unknown';
        const contact = query.data.value;
        return contact?.fullname || [contact?.firstname, contact?.lastname].filter(Boolean).join(' ') || 'Unknown';
    });
    return { ...query, label };
};

export const useUser = () => {
    return useQuery(['user'], () => getUserDetails('me'));
};

export const useUserRoles = () => {
    const store = useStore();
    return computed(() => store.getters['userRoles']);
};

export const useActiveUsers = () => {
    const store = useStore();
    store.dispatch('loadUsers');
    return computed(() => store.getters['activeUsers']);
};

export const useUserCompanyFunctions = () => {
    const store = useStore();
    store.dispatch('userCompanyFunction/fetchUserCompanyFunctions');
    return computed<Record<string, IUserCompanyFunction>>(() => store.getters['userCompanyFunction/companyFunctionsByUser']);
};

export const useContactSelectorModal = () => {
    const ContactSelectorModal = defineComponent({
        name: 'ContactSelectorModal',
        props: {
            title: {
                type: String,
                default: 'Select Contacts',
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            contactTypes: {
                type: Array,
                default: () => [],
            },
            estateId: {
                type: Number,
                required: false,
            },
            allowCreateExternal: {
                type: Boolean,
                default: false,
            },
        },
        emits: ['confirm', 'cancel', 'create-external'],
        setup(props, { emit }) {
            const selectedContacts = ref<IContactPerson[]>([]);

            const handleContactSelected = (contactOrContacts: IContactPerson | IContactPerson[]) => {
                selectedContacts.value = arrify(contactOrContacts);
            };

            const confirmSelection = () => {
                emit('confirm', selectedContacts.value);
            };

            const cancelSelection = () => {
                emit('cancel');
            };

            const openCreateExternal = () => {
                emit('create-external');
            };

            return () =>
                h('div', { class: 'w-full rounded-lg bg-white p-6 flex flex-col justify-between' }, [
                    h('div', [
                        h('h3', { class: 'mb-4 text-lg font-semibold' }, props.title),
                        h(
                            ContactQuickSearch,
                            {
                                clearable: true,
                                contactTypes: props.contactTypes,
                                estateId: props.estateId,
                                multiple: props.multiple,
                                'clear-after-select': true,
                                'disable-condo-field': true,
                                onContactSelected: handleContactSelected,
                            },
                            {
                                action: props.allowCreateExternal
                                    ? () =>
                                          h('div', { class: 'px-2 py-2 border-t border-slate-200' }, [
                                              h(
                                                  NButton,
                                                  {
                                                      text: true,
                                                      type: 'primary',
                                                      size: 'small',
                                                      onClick: openCreateExternal,
                                                      class: 'w-full',
                                                  },
                                                  {
                                                      icon: () => h('i', { class: 'fas fa-user-plus' }),
                                                      default: () => 'Create External Signer',
                                                  },
                                              ),
                                          ])
                                    : undefined,
                            },
                        ),
                    ]),
                    h('div', { class: 'mt-4 flex justify-end gap-2' }, [
                        h(
                            NButton,
                            {
                                onClick: cancelSelection,
                                size: 'small',
                            },
                            'Cancel',
                        ),
                        h(
                            NButton,
                            {
                                onClick: confirmSelection,
                                disabled: !selectedContacts.value.length,
                                size: 'small',
                                type: 'primary',
                            },
                            'Confirm',
                        ),
                    ]),
                ]);
        },
    });

    const openContactSelector = (params?: {
        title?: string;
        multiple?: boolean;
        contactTypes?: string[];
        estateId?: number;
        width?: number; // default 600
        maxWidth?: number; // default 600
        height?: number; // default not set
        allowCreateExternal?: boolean;
        onCreateExternal?: () => void;
    }): Promise<{ contacts: IContactPerson[]; close: () => void } | null> => {
        return new Promise(resolve => {
            const modalInstance = showModal(ContactSelectorModal, {
                width: params?.width ?? 600,
                maxWidth: params?.maxWidth ?? 600,
                ...(Boolean(params?.height) && { height: params!.height }),
                props: {
                    title: params?.title || 'Select Contacts',
                    multiple: params?.multiple ?? false,
                    contactTypes: params?.contactTypes ?? [],
                    estateId: params?.estateId,
                    allowCreateExternal: params?.allowCreateExternal ?? false,
                },
                events: {
                    confirm: (contacts: IContactPerson[]) => {
                        resolve({
                            contacts,
                            close: () => {
                                modalInstance.close();
                            },
                        });
                    },
                    cancel: () => {
                        modalInstance.close();
                        resolve(null);
                    },
                    'create-external': () => {
                        if (params?.onCreateExternal) {
                            params.onCreateExternal();
                        }
                    },
                },
            });
        });
    };

    return {
        openContactSelector,
    };
};
